import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

interface SessionTrackerProps {
  flowId: string;
}

export const SessionTracker = ({ flowId }: SessionTrackerProps) => {
  // Use refs to maintain state across renders without causing re-renders
  const sessionIdRef = useRef<string | null>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isEmbedded = window.self !== window.top;

  // Skip tracking completely if not embedded
  if (!isEmbedded) {
    return null;
  }

  const getLocationInfo = useCallback(async () => {
    // Check if we've already tried recently to avoid rate limiting
    const lastAttempt = localStorage.getItem('location_last_attempt');
    const now = Date.now();
    if (lastAttempt && (now - parseInt(lastAttempt)) < 300000) { // 5 minutes
      return {
        city: 'Unknown',
        region: 'Unknown',
        country: 'Unknown'
      };
    }

    try {
      // Use a more reliable geolocation service with CORS support
      const response = await fetch('https://ipapi.co/json/', {
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      localStorage.setItem('location_last_attempt', now.toString());
      
      return {
        city: data.city || 'Unknown',
        region: data.region || 'Unknown',
        country: data.country_name || 'Unknown'
      };
    } catch (error) {
      localStorage.setItem('location_last_attempt', now.toString());
      
      // Fallback to browser geolocation or default values
      return {
        city: 'Unknown',
        region: 'Unknown', 
        country: 'Unknown'
      };
    }
  }, []);

  const trackConversion = useCallback(async (email?: string, amount?: number | null) => {
    if (!sessionIdRef.current) return;

    try {
      const locationInfo = await getLocationInfo();
      console.log('Recording conversion for embedded flow');

      await supabase.from('flow_analytics').insert({
        flow_id: flowId,
        event_type: 'conversion',
        email: email,
        referrer: document.referrer || 'direct',
        is_embedded: true,
        user_agent: navigator.userAgent,
        path: window.location.pathname,
        country: locationInfo?.country || 'Unknown',
        user_identifier: sessionIdRef.current,
        view_date: new Date().toISOString().split('T')[0],
        amount: amount ?? null,
      });
    } catch (error) {
      console.error('Error recording conversion:', error);
    }
  }, [flowId, getLocationInfo]);

  const createOrUpdateSession = useCallback(async () => {
    try {
      if (!sessionIdRef.current) {
        sessionIdRef.current = uuidv4();
      }

      const now = new Date().toISOString();

      // Get location info
      const locationInfo = await getLocationInfo();

      const { error } = await supabase.from('active_sessions').upsert(
        {
          id: sessionIdRef.current,
          flow_id: flowId,
          user_identifier: sessionIdRef.current,
          page_url: window.location.href,
          last_seen: now,
          ...(locationInfo && {
            city: locationInfo.city,
            region: locationInfo.region,
            country: locationInfo.country
          })
        },
        {
          onConflict: 'id',
          ignoreDuplicates: false,
        }
      );

      if (error) {
        throw error;
      }

      // Also record analytics event for first view
      if (!updateTimeoutRef.current) {
        // This is the first call, so record a view event
        await supabase.from('flow_analytics').insert({
          flow_id: flowId,
          event_type: 'view',
          referrer: document.referrer || 'direct',
          is_embedded: true,
          user_agent: navigator.userAgent,
          path: window.location.pathname,
          country: locationInfo?.country || 'Unknown',
          user_identifier: sessionIdRef.current,
          view_date: new Date().toISOString().split('T')[0]
        });
      }

      // Schedule next update
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(createOrUpdateSession, 15000);
    } catch (error) {
      // Retry after 10 seconds on error to avoid overwhelming the server
      updateTimeoutRef.current = setTimeout(createOrUpdateSession, 10000);
    }
  }, [flowId, getLocationInfo]);

  const cleanup = useCallback(async () => {
    if (!sessionIdRef.current) return;

    try {
      // Instead of deleting, mark the session as inactive by setting last_seen
      // This helps with analytics while still removing from active view
      const { error } = await supabase
        .from('active_sessions')
        .update({
          last_seen: new Date(Date.now() - 6 * 60 * 1000).toISOString() // Set to 6 minutes ago to ensure it's filtered out
        })
        .eq('id', sessionIdRef.current);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error cleaning up session:', error);
    } finally {
      sessionIdRef.current = null;
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    // Listen for custom events from the payment flow
    const handleConversionEvent = (event: CustomEvent) => {
      const email = event.detail?.email;
      const amount = event.detail?.amount;
      trackConversion(email, amount);
    };

    // Add event listener for custom 'wtc-conversion' event
    window.addEventListener('wtc-conversion', handleConversionEvent as EventListener);

    // Start tracking immediately
    createOrUpdateSession();

    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        cleanup();
      } else {
        createOrUpdateSession();
      }
    };

    // Handle page close/refresh
    const handleUnload = () => cleanup();

    // Set up event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleUnload);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('wtc-conversion', handleConversionEvent as EventListener);
      cleanup();
    };
  }, [createOrUpdateSession, cleanup, trackConversion]);

  return null;
}; 